import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faChevronDown, faSearch } from '@fortawesome/pro-regular-svg-icons'
import { Flex, Skeleton } from 'antd'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import EmptyState from 'src/components/EmptyState'
import Text, { TextSize } from 'src/components/Typography/Text'
import useDisclosure from 'src/hooks/use-disclosure'

import VStack from '../Flex/VStack'
import FormControl from '../FormControl/FormControl'
import InputBase from '../Input/InputBase'
import { useMenuKeyboardShortcuts } from '../Menu/use-menu-keyboard-shortcuts'
import Popover from '../Popover/Popover'
import Tooltip from '../Tooltip/Tooltip'

import SelectMenuContent from './SelectMenuContent'
import SelectMenuItem from './SelectMenuItem'
import SelectMenuItemGroupLabel from './SelectMenuItemGroupLabel'
import { SelectBaseProps, SelectOption } from './types'
import { useSelectKeyboardShortcuts } from './use-select-keyboard-shortcuts'
import { useCreateOption, useFilteredOptions, useShowAsyncStartSearchingEmptyState } from './util'

const StyledLoadingState = styled(Flex).attrs({
  vertical: true,
  gap: '6px',
})`
  width: 100%;
  padding: 6px;
  .ant-skeleton-input {
    height: 32px !important;
    width: 100% !important;
  }
`

type Props<TValue> = SelectBaseProps<TValue> & {
  onSelectedOptionChange: (
    selectedOption: SelectOption<TValue> | undefined
  ) => void | Promise<unknown>
  selectedOption: SelectOption<TValue> | undefined
}
const Select = <TValue,>({
  ariaLabel,
  autoFocus,
  caption,
  errorMessage,
  filterOption,
  getIsOptionDisabled,
  getOptionKey,
  isAsync = false,
  isClearable,
  isCreatable,
  isDisabled,
  isInvalid,
  isLoading,
  isReadOnly,
  isRequired,
  hideRequiredAsterisk,
  itemLayout = 'column',
  label,
  prefix: prefixProp,
  prefixIcon,
  placeholder,
  onBlur: propOnBlur,
  onCreateOption,
  onSearchQueryChange,
  onSelectedOptionChange,
  options,
  selectedOption,
  size,
  shouldShowSelectedOptionCustomDisplay,
  suffixIcon,
  width = '100%',
}: Props<TValue>) => {
  const [id] = useState(uuid())
  const [menuId] = useState(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined)
  const [activeIndex, setActiveIndex] = useState(0)
  const { isOpen: hasFocus, open: onFocus, close: onBlur } = useDisclosure()
  const { isOpen, open, close } = useDisclosure()

  const [hasAutoFocused, setHasAutoFocused] = useState(false)
  useEffect(() => {
    if (autoFocus && !isLoading && !hasAutoFocused) {
      open()
      setHasAutoFocused(true)
    }
  }, [autoFocus, hasAutoFocused, isLoading, open])

  const filteredOptions = useFilteredOptions({
    isAsync,
    options,
    searchQuery,
    filterOption,
  })

  const handleOpen = useCallback(() => {
    if (isDisabled) {
      return
    }
    open()
    inputRef.current?.focus()
  }, [isDisabled, open])

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur()
      propOnBlur?.(e)
      // Only clear the search query when an option is already selected so its
      // label is shown in the input after blur. When nothing is selected, we
      // preserve the typed query so users don't lose their search text if they
      // accidentally click away before picking an option.
      if (selectedOption) {
        setSearchQuery(undefined)
      }
      close()
    },
    [close, onBlur, propOnBlur, selectedOption]
  )

  const handleClear = useCallback(() => {
    void onSelectedOptionChange(undefined)
    onFocus()
  }, [onFocus, onSelectedOptionChange])

  const handleValueChange = useCallback(
    (value: string | undefined) => {
      setSearchQuery(value)
      setActiveIndex(0)
      open()
    },
    [open]
  )

  useSelectKeyboardShortcuts({
    hasFocus,
    isOpen,
    onClose: close,
    onOpen: handleOpen,
  })

  const handleOptionSelected = useCallback(
    (o: SelectOption<TValue>) => {
      if (o.disabled || getIsOptionDisabled?.(o)) {
        return
      }
      void onSelectedOptionChange(o)
      if (o.keepOpen) {
        return
      }
      close()
      setSearchQuery(undefined)
      setTimeout(() => {
        inputRef.current?.select()
      })
    },
    [getIsOptionDisabled, close, onSelectedOptionChange]
  )

  const handleCreateOptionClicked = useCallback(() => {
    if (!searchQuery) {
      return
    }
    onCreateOption?.(searchQuery)
    close()
    setSearchQuery(undefined)
  }, [close, onCreateOption, searchQuery])

  const activeOption = filteredOptions[activeIndex]
  const actionCount = filteredOptions.length + (isCreatable ? 1 : 0)
  const createActionIndex = isCreatable ? actionCount - 1 : -1
  const { menuItemRefs } = useMenuKeyboardShortcuts({
    activeIndex,
    count: actionCount,
    getIsItemDisabled: (index: number) => {
      if (index === createActionIndex) {
        return false
      }
      const option = options[index]
      if (!option) {
        return true
      }
      return !!option.disabled || !!getIsOptionDisabled?.(option)
    },
    isEnabled: isOpen,
    onEscape: () => {
      close()
      if (selectedOption) {
        const selectedOptionKey = getOptionKey(selectedOption)
        setActiveIndex(options.findIndex((o) => getOptionKey(o) === selectedOptionKey))
      } else {
        setActiveIndex(0)
      }
    },
    onSelect: () => {
      if (activeIndex === createActionIndex) {
        handleCreateOptionClicked()
      }
      if (!activeOption) {
        return
      }
      handleOptionSelected(activeOption)
    },
    setActiveIndex,
  })

  useEffect(() => {
    // Reset active index when options change
    setActiveIndex(0)
  }, [filteredOptions])

  useEffect(() => {
    // Report changes in value
    onSearchQueryChange?.(searchQuery ?? '')
  }, [onSearchQueryChange, searchQuery])

  const getOptionId = useCallback(
    (option: SelectOption<TValue>) => `${id}-${getOptionKey(option)}`,
    [getOptionKey, id]
  )

  const createOption = useCreateOption({
    activeIndex,
    createActionIndex,
    isCreatable,
    onCreateOption: handleCreateOptionClicked,
    searchQuery,
    setActiveIndex,
  })

  const renderedOptions = useMemo(
    () => [
      ...filteredOptions.map((o, index) => (
        <Fragment key={`${o.group}-${getOptionKey(o)}`}>
          <SelectMenuItemGroupLabel index={index} option={o} options={filteredOptions} />
          <Tooltip content={o.tooltip}>
            <div className="relative">
              <SelectMenuItem
                id={getOptionId(o)}
                $isDisabled={o.disabled || getIsOptionDisabled?.(o)}
                key={getOptionKey(o)}
                onClick={() => handleOptionSelected(o)}
                onMouseOver={() => setActiveIndex(index)}
                ref={(el) => menuItemRefs.current.set(index, el)}
                {...(index === activeIndex
                  ? {
                      ['data-selected']: true, // controls option remaining highlighted when submenu is expanded
                      ['aria-selected']: true,
                    }
                  : {})}
              >
                {o.customDisplay ? (
                  o.customDisplay(o.value)
                ) : (
                  <VStack gap={1}>
                    <Text
                      color="inherit"
                      strong={!!selectedOption && getOptionKey(o) === getOptionKey(selectedOption)}
                    >
                      {o.label}
                    </Text>
                    {o.sublabel && (
                      <Text
                        colorToken={
                          index === activeIndex ? 'colorPrimaryBase' : 'colorTextSecondary'
                        }
                        size={TextSize.Small}
                        strong={
                          !!selectedOption && getOptionKey(o) === getOptionKey(selectedOption)
                        }
                      >
                        {o.sublabel}
                      </Text>
                    )}
                  </VStack>
                )}
              </SelectMenuItem>
            </div>
          </Tooltip>
        </Fragment>
      )),
      ...(createOption ? [createOption] : []),
    ],
    [
      activeIndex,
      createOption,
      filteredOptions,
      getIsOptionDisabled,
      getOptionId,
      getOptionKey,
      handleOptionSelected,
      menuItemRefs,
      selectedOption,
    ]
  )

  const showAsyncStartSearchingEmptyState = useShowAsyncStartSearchingEmptyState({
    isAsync,
    isLoading,
    searchQuery,
  })

  const isSelectedOptionCustomDisplayVisible =
    shouldShowSelectedOptionCustomDisplay && !!selectedOption?.customDisplay

  const prefix = useMemo(() => {
    if (isSelectedOptionCustomDisplayVisible) {
      return selectedOption?.customDisplay?.(selectedOption.value)
    }
    return prefixProp
  }, [isSelectedOptionCustomDisplayVisible, prefixProp, selectedOption])

  const inputValue = useMemo(() => {
    const selectedOptionLabel = selectedOption?.label ?? ''
    if (hasFocus) {
      return searchQuery ?? (shouldShowSelectedOptionCustomDisplay ? '' : selectedOptionLabel)
    }
    // When unfocused with no selected option, show the preserved search query
    // so users can see what they had typed if they click away without selecting.
    if (!selectedOption && searchQuery !== undefined) {
      return searchQuery
    }
    return shouldShowSelectedOptionCustomDisplay ? '' : selectedOptionLabel
  }, [hasFocus, shouldShowSelectedOptionCustomDisplay, searchQuery, selectedOption])

  return (
    <FormControl
      caption={caption}
      errorMessage={errorMessage}
      id={id}
      isInvalid={isInvalid}
      isRequired={isRequired}
      hideRequiredAsterisk={hideRequiredAsterisk}
      label={label}
      width={width}
    >
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <Popover isOpen={isOpen} onOpen={handleOpen} onClose={close}>
          <Popover.Trigger
            asChild
            onClick={(e) => {
              if (isOpen) {
                e.preventDefault() // Prevent closing when clicking to different parts of the input text
              }
            }}
          >
            <div style={{ width: '100%' }}>
              <InputBase
                ariaActiveDescendant={activeOption ? getOptionId(activeOption) : undefined}
                ariaControls={menuId}
                ariaDescribedBy={ariaDescribedBy}
                ariaExpanded={isOpen}
                ariaInvalid={ariaInvalid}
                ariaLabel={ariaLabel}
                ariaLive={ariaLive}
                autoFocus={autoFocus}
                hasValue={!!selectedOption}
                id={id}
                isClearable={isClearable}
                isDisabled={isDisabled}
                isInvalid={isInvalid}
                isLoading={isLoading}
                isReadOnly={isReadOnly}
                isRequired={isRequired}
                onBlur={handleBlur}
                onClear={handleClear}
                onFocus={onFocus}
                onValueChange={handleValueChange}
                placeholder={isSelectedOptionCustomDisplayVisible ? '' : placeholder}
                prefix={prefix}
                prefixIcon={prefixIcon}
                ref={inputRef}
                role="combobox"
                size={size}
                suffixIcon={suffixIcon === null ? undefined : suffixIcon || faChevronDown}
                value={inputValue}
              />
            </div>
          </Popover.Trigger>
          <SelectMenuContent id={menuId} $layout={itemLayout}>
            {renderedOptions}
            {!renderedOptions.length && !isLoading && (
              <EmptyState hideBorder size="sm">
                <EmptyState.Image icon={showAsyncStartSearchingEmptyState ? faSearch : undefined} />
                <EmptyState.Message>
                  {showAsyncStartSearchingEmptyState
                    ? 'Type to start searching'
                    : isCreatable
                      ? 'Type and press Enter to add'
                      : 'No options found'}
                </EmptyState.Message>
              </EmptyState>
            )}
            {!filteredOptions.length && isLoading && (
              <StyledLoadingState>
                <Skeleton.Input active />
                <Skeleton.Input active />
                <Skeleton.Input active />
              </StyledLoadingState>
            )}
          </SelectMenuContent>
        </Popover>
      )}
    </FormControl>
  )
}

export default React.memo(Select) as typeof Select
